import { Blackjack, EventQueue } from 'game-logic';
import { BlackjackGameRunner } from '../../model/blackjack-game-runner';
import { InterfaceEvents } from '../services/game-services';

export class BetController {
  public get totalBetAmount(): number {
    return this.betChips.reduce((sum, value) => sum + value, 0);
  }
  public get currentBetChips(): ReadonlyArray<Blackjack.ChipDenomination> {
    return this.betChips;
  }

  private betChips: Blackjack.ChipDenomination[] = [];

  constructor(
    private blackjackGameRunner: BlackjackGameRunner,
    private interfaceEventQueue: EventQueue<InterfaceEvents>,
    private gameListener: Blackjack.BlackjackListener,
  ) {}

  public clearCurrentBet(): void {
    this.betChips = [];

    this.betChanged();
    this.interfaceEventQueue.enqueue({
      type: 'onBetCleared',
      instant: false,
      payload: { totalBetAmount: this.totalBetAmount, currentBalance: this.chipsAmount() },
    });
  }

  public increaseCurrentBet(chip: Blackjack.ChipDenomination): void {
    const newBetAmount = this.totalBetAmount + chip;
    if (newBetAmount > this.chipsAmount()) {
      return;
    }

    this.betChips.push(chip);

    this.betChanged();
    this.interfaceEventQueue.enqueue({
      type: 'onBetIncreased',
      instant: false,
      payload: { chip, totalBetAmount: this.totalBetAmount, currentBalance: this.chipsAmount() },
    });
  }

  public betAllIn(): void {
    const chips = chipsFromTotalAmount(this.allInAmount());
    this.betChips = chips;

    this.betChanged();
    this.interfaceEventQueue.enqueue({
      type: 'onBetAllIn',
      instant: false,
      payload: { chips, totalBetAmount: this.totalBetAmount, currentBalance: this.chipsAmount() },
    });
  }

  private betChanged(): void {
    // Inform the front-end HTML code
    this.gameListener.onBetChanged(this.totalBetAmount);
  }

  private chipsAmount(): number {
    return this.blackjackGameRunner.currentGame?.chipsAmount ?? 0;
  }

  private allInAmount(): number {
    return this.blackjackGameRunner.currentGame?.allInAmount ?? 0;
  }
}

function chipsFromTotalAmount(amount: number): Blackjack.ChipDenomination[] {
  const denominations = Object.values(Blackjack.ChipDenomination).filter(
    (value) => typeof value === 'number',
  ) as number[];

  // Sort denominations in descending order
  denominations.sort((a, b) => b - a);

  const result: Blackjack.ChipDenomination[] = [];
  for (const denom of denominations) {
    while (amount >= denom) {
      result.push(denom as unknown as Blackjack.ChipDenomination);
      amount -= denom;
    }
  }

  // Sort results so smallest appears first
  result.sort((a, b) => a - b);

  return result;
}
